import { Theme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  TextField,
  Typography,
  Modal,
  IconButton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { Autocomplete } from 'ui/components/Autocomplete/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import { sendEmail } from 'services/email/api';
import { Email } from 'services/email/types';
import { getActiveUser } from 'services/user';
import { useFlags } from 'helpers/useFlags';

export interface CreateFormModalSubmitData {
  startingPoint: 'scratch' | 'template';
  templateId?: string;
  templateName: string;
}

interface TemplateOption {
  id: string;
  name: string;
  // Add any other relevant template properties if needed
}

export interface CreateFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFormModalSubmitData) => void;
  templates?: TemplateOption[]; // Optional: if templates are fetched async or might not exist
  isLoadingTemplates?: boolean;
  defaultTemplateName?: string;
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
};

export const CreateFormModal: React.FC<CreateFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  templates = [],
  isLoadingTemplates = false,
  defaultTemplateName = 'Sales order template',
}) => {
  const [startingPoint, setStartingPoint] = useState<'scratch' | 'template'>(
    'scratch'
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>(defaultTemplateName);
  const activeUser = useSelector(getActiveUser);
  const flags = useFlags();

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setStartingPoint('scratch');
      setTemplateName(defaultTemplateName);
      // Reset selection; the other useEffect will handle setting the default
      // when templates load for the first time.
      setSelectedTemplateId('');
    }
  }, [open, defaultTemplateName]);

  useEffect(() => {
    // Ensure a template is selected if 'template' starting point is chosen and templates are available
    if (
      startingPoint === 'template' &&
      templates.length > 0 &&
      !selectedTemplateId
    ) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [startingPoint, templates, selectedTemplateId]);

  const handleStartingPointChange = (
    event: React.MouseEvent<HTMLDivElement>,
    newValue: 'scratch' | 'template'
  ) => {
    setStartingPoint(newValue);
    if (newValue === 'scratch') {
      setTemplateName('New blank template'); // Default name for scratch forms
      setSelectedTemplateId('');
    }
    if (newValue === 'template') {
      setTemplateName('New template');
      // If no template is selected yet and templates exist, select the first one
      if (!selectedTemplateId && templates.length > 0) {
        setSelectedTemplateId(templates[0].id);
      }
    }
  };

  const sendPMNotification = async (
    templateData: CreateFormModalSubmitData
  ) => {
    try {
      const pmEmail = process.env.REACT_APP_PM_EMAIL;
      const pmEmailEnabled = flags.templateNotifications;

      // Only send email if PM email is configured and flag is enabled
      if (!pmEmail || !pmEmailEnabled) {
        console.log('Skipping notification');
        return;
      }

      // Get user information
      const user = activeUser?.user;
      const userDisplayName =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.name || 'Unknown User';
      const userEmail = user?.email || 'No email available';
      const userTenant = activeUser?.tenant || 'Unknown tenant';
      const isAdmin = activeUser?.isAdmin ? 'Yes' : 'No';

      const emailData: Email = {
        to: pmEmail,
        cc: null,
        bcc: null,
        subject: 'New Template Created - Feature Usage Notification',
        body: `
          <h3>New Template Created</h3>
          <p>A customer has created a new template using the template feature.</p>
          
          <p><strong>User Information:</strong></p>
          <ul>
            <li><strong>User Name:</strong> ${userDisplayName}</li>
            <li><strong>User Email:</strong> ${userEmail}</li>
            <li><strong>Tenant:</strong> ${userTenant}</li>
            <li><strong>Admin User:</strong> ${isAdmin}</li>
          </ul>
          
          <p><strong>Template Details:</strong></p>
          <ul>
            <li><strong>Template Name:</strong> ${
              templateData.templateName
            }</li>
            <li><strong>Starting Point:</strong> ${
              templateData.startingPoint === 'scratch'
                ? 'From Scratch'
                : 'From Existing Template'
            }</li>
            ${
              templateData.templateId
                ? `<li><strong>Base Template ID:</strong> ${templateData.templateId}</li>`
                : ''
            }
            <li><strong>Created At:</strong> ${new Date().toLocaleString()}</li>
          </ul>
          
          <p>This notification was sent automatically to track template feature adoption.</p>
        `,
      };

      await sendEmail(emailData, undefined, undefined, true);
    } catch (error) {
      // Log error but don't block template creation
      console.error('Failed to send PM notification email:', error);
    }
  };

  const handleSubmit = async () => {
    const templateData = {
      startingPoint,
      templateId: startingPoint === 'template' ? selectedTemplateId : undefined,
      templateName,
    };

    // Send PM notification (don't await to avoid blocking UI)
    sendPMNotification(templateData);

    // Continue with normal template creation
    onSubmit(templateData);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="create-template-modal-title"
    >
      <Box sx={modalStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            id="create-template-modal-title"
            variant="h6"
            component="h2"
          >
            Create Template
          </Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body1" gutterBottom sx={{ marginBottom: 2 }}>
          Choose a starting point:
        </Typography>

        <Card
          sx={(theme: Theme) => ({
            p: 2,
            mb: 2,
            border: '1px solid',
            borderColor:
              startingPoint === 'scratch'
                ? theme.palette.primary.main
                : theme.palette.grey[300],
            backgroundColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            position: 'relative', // For the check icon
            transition: 'border-color 0.2s ease-in-out',
            '&:hover': {
              borderColor:
                startingPoint === 'scratch'
                  ? theme.palette.primary.dark
                  : theme.palette.grey[500],
            },
            height: 100,
          })}
          onClick={(e) => handleStartingPointChange(e, 'scratch')}
          elevation={startingPoint === 'scratch' ? 3 : 0}
        >
          {startingPoint === 'scratch' && (
            <CheckCircleIcon
              sx={(theme: Theme) => ({
                position: 'absolute',
                top: theme.spacing(1),
                right: theme.spacing(1),
                fontSize: '1.25rem', // Smaller check icon
                color: theme.palette.primary.main,
              })}
            />
          )}
          <Box
            sx={(theme: Theme) => ({
              mr: 2,
              p: 1,
              bgcolor: theme.palette.grey[100],
              borderRadius: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <DescriptionOutlinedIcon
              sx={{ fontSize: 24, color: 'action.active' }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Start from scratch
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Begin with a blank template.
            </Typography>
          </Box>
        </Card>

        <Card
          sx={(theme: Theme) => ({
            p: 2,
            mb: 4,
            border: '1px solid',
            borderColor:
              startingPoint === 'template'
                ? theme.palette.primary.main
                : theme.palette.grey[300],
            backgroundColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            position: 'relative', // For the check icon
            transition: 'border-color 0.2s ease-in-out',
            '&:hover': {
              borderColor:
                startingPoint === 'template'
                  ? theme.palette.primary.dark
                  : theme.palette.grey[500],
            },
            height: 100,
          })}
          onClick={(e) => handleStartingPointChange(e, 'template')}
          elevation={startingPoint === 'template' ? 3 : 0}
        >
          {startingPoint === 'template' && (
            <CheckCircleIcon
              sx={(theme: Theme) => ({
                position: 'absolute',
                top: theme.spacing(1),
                right: theme.spacing(1),
                fontSize: '1.25rem', // Smaller check icon
                color: theme.palette.primary.main,
              })}
            />
          )}
          <Box
            sx={(theme: Theme) => ({
              mr: 2,
              p: 1,
              bgcolor: theme.palette.grey[100],
              borderRadius: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            })}
          >
            <AutoAwesomeOutlinedIcon
              sx={{ fontSize: 24, color: 'action.active' }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Start from Template
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use a pre-built template to save time.
            </Typography>
          </Box>
        </Card>

        <Autocomplete
          label="Select Template"
          options={templates}
          getOptionLabel={(option) => option.name}
          value={templates.find((t) => t.id === selectedTemplateId) || null}
          onChange={(event, newValue) => {
            setSelectedTemplateId(newValue ? newValue.id : '');
          }}
          loading={isLoadingTemplates}
          disabled={isLoadingTemplates || startingPoint !== 'template'}
          disableClearable
        />
        <TextField
          className="redesign"
          variant="standard"
          fullWidth
          label="Template Name"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />

        <Box display="flex" justifyContent="flex-end" mt="auto">
          <FBOButton
            variant="secondary"
            color="neutral"
            onClick={onClose}
            sx={{ mr: 1 }}
            data-qa="create-template-modal-cancel-button"
          >
            Cancel
          </FBOButton>
          <FBOButton
            variant="primary"
            color="positive"
            onClick={handleSubmit}
            loading={isLoadingTemplates}
            data-qa="create-template-modal-continue-button"
            disabled={
              (startingPoint === 'template' &&
                (!selectedTemplateId || templates.length === 0)) ||
              !templateName.trim()
            }
          >
            Create
          </FBOButton>
        </Box>
      </Box>
    </Modal>
  );
};
